import { PropsWithChildren, MouseEvent, ReactNode } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { GlowDivider, GlowIcon, GlowTitle, GlowClickable } from '../../../../src';
import { PropsWithClassName, PropsWithFontFamily, PropsWithId, PropsWithTestId } from '../../props';
import { Icons } from '../../res/icons';

type HeaderBaseProps = Partial<PropsWithFontFamily> & {
    title?: string;
    fallback?: ReactNode;
    onClick?: (event: MouseEvent<Element>) => void;
};
type IconProps =
    | { hasRightIcon: boolean; onClickRightIcon: (e: MouseEvent<Element>) => void }
    | { hasRightIcon?: boolean; onClickRightIcon?: undefined };

type HeaderProps = HeaderBaseProps & IconProps;

const Header: Component<HeaderProps> = ({
    title,
    onClick,
    onClickRightIcon,
    hasRightIcon,
    fontFamily = 'brand',
    fallback,
}) => {
    return (
        <header
            className={
                'flex flex-row justify-between items-center p-1 gap-1 border-b-medium border-b-border-subtle border-solid min-h-headerFixedNavDesktop'
            }
        >
            {fallback ?? (
                <>
                    {hasRightIcon && onClickRightIcon && (
                        <GlowClickable
                            onClick={onClickRightIcon}
                            content={{
                                icon: (
                                    <GlowIcon
                                        Icon={Icons.ChevronRight}
                                        type={'large'}
                                        className={'text-text-primary rotate-180'}
                                    />
                                ),
                            }}
                        />
                    )}
                    <GlowTitle
                        text={title}
                        fontSize={'title4'}
                        className={'flex-1 text-left'}
                        fontFamily={fontFamily}
                        TagName={'h2'}
                    />
                </>
            )}

            {onClick && (
                <GlowClickable
                    onClick={onClick}
                    content={{
                        icon: <GlowIcon Icon={Icons.Cross} type={'large'} className={'text-text-primary'} />,
                    }}
                />
            )}
        </header>
    );
};

type FooterProps = PropsWithChildren & PropsWithClassName;

const Footer: Component<FooterProps> = ({ children }) => {
    return (
        <footer className={'z-10 w-full bg-background-l0'}>
            <GlowDivider />
            <div className={'flex items-center justify-center p-1 flex-col gap-0.5'}>{children}</div>
        </footer>
    );
};

type ContentProps = PropsWithChildren &
    PropsWithClassName &
    Partial<PropsWithTestId> &
    PropsWithId & {
        contentClassName?: string;
    };

const Content: Component<ContentProps> = ({ children, className, contentClassName, dataTestId }) => {
    return (
        <div
            className={clsx('flex flex-1 flex-col h-full overflow-x-auto relative', className)}
            data-testid={dataTestId}
        >
            <div className={clsx('w-full absolute flex flex-col gap-1.5 p-1 h-full', contentClassName)}>{children}</div>
        </div>
    );
};

type ContainerProps = PropsWithChildren & PropsWithClassName & PropsWithId & HeaderProps;

const Container: Component<ContainerProps> = ({
    children,
    id,
    className,
    onClick,
    onClickRightIcon,
    hasRightIcon = false,
    title,
    fallback,
}) => {
    return (
        <div
            id={id}
            className={clsx(
                'h-fullViewport w-fullViewport tablet:w-sideMenuTablet flex flex-col bg-background-l3',
                className,
            )}
        >
            <Header
                title={title}
                onClick={onClick}
                onClickRightIcon={onClickRightIcon}
                hasRightIcon={hasRightIcon}
                fallback={fallback}
            />
            {children}
        </div>
    );
};

export const GlowModal = {
    Container,
    Content,
    Footer,
};
